import { readableNumber } from "./text"
import { platformSlugs } from "./topicStats"

const PLATFORM_LINK_PRIORITY = [
  "slack",
  "discord",
  "reddit",
  "linkedin",
  "facebook",
  "independent",
]

function joinNames(items) {
  if (!items.length) return ""
  if (items.length === 1) return items[0]
  if (items.length === 2) return `${items[0]} and ${items[1]}`
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`
}

function communityWord(count) {
  return count === 1 ? "community" : "communities"
}

const SAMPLE_SMALL = 30
const SAMPLE_VERY_SMALL = 15

function getSampleSizeNote(total) {
  if (!total || total >= SAMPLE_SMALL) return null
  if (total < SAMPLE_VERY_SMALL) {
    return "We only have a handful of listings so far, so treat these numbers as directional."
  }
  return "This is a smaller slice of Hive Index's database, so read trends as directional."
}

function datasetOpener(total) {
  if (total < SAMPLE_VERY_SMALL) return "Among the groups we analyzed, "
  if (total < SAMPLE_SMALL) return "In the dataset, "
  return ""
}

function hasMemberOutlier(stats) {
  if (!stats.averageMembers || !stats.medianMembers) return false
  const avg = Math.round(stats.averageMembers)
  const med = Math.round(stats.medianMembers)
  return avg >= med * 1.5
}

function memberOutlierCopy(stats) {
  const avg = readableNumber(Math.round(stats.averageMembers))
  const med = readableNumber(Math.round(stats.medianMembers))
  return `The average is much higher than the median (${avg} vs ${med}), which usually means a few large groups are pulling the average up.`
}

function buildReportMeta({ topic, stats }) {
  const topicName = topic.name
  const total = stats.total

  if (!total) {
    return {
      seoTitle: `${topicName} Communities Report | Hive Index`,
      metaDescription: `Community report for ${topicName} on Hive Index. Browse the topic directory or submit a community we should list.`,
    }
  }

  const topPlatform = stats.platformBreakdown[0]
  let metaDescription = `${total} ${topicName} communities on Hive Index: platforms, size, access, features, ratings, and click patterns.`

  if (topPlatform) {
    metaDescription = `${total} ${topicName} communities on Hive Index. ${topPlatform.percent}% are on ${topPlatform.name}. See size, access, features, ratings, and trends.`
  }

  return {
    seoTitle: `${topicName} Communities Report: Stats, Platforms, Trends & Data | Hive Index`,
    metaDescription,
  }
}

function buildHeroContent({ topic, stats, updatedDate }) {
  const topicName = topic.name
  const total = stats.total
  const lower = topicName.toLowerCase()

  if (!total) {
    return {
      lead: `Hive Index does not yet have ${topicName} communities in the database.`,
      subtitle: null,
      sourceLine: "Source: Hive Index community database",
    }
  }

  const lead = `We analyzed ${total} ${lower} ${communityWord(
    total
  )} listed on Hive Index to understand where people gather, what these groups offer, and which communities attract the most interest.`

  const sourceParts = [
    "Hive Index community database",
    `Tagged ${topicName}`,
    `${total} ${communityWord(total)}`,
  ]
  if (updatedDate) {
    sourceParts.push(`Updated ${updatedDate}`)
  }

  return {
    lead,
    subtitle: `${topicName} communities report`,
    sourceLine: sourceParts.join(" · "),
    updatedNote: updatedDate ? null : "Updated periodically",
  }
}

function buildExecutiveStatCards({ stats }) {
  const cards = [
    {
      key: "total",
      label: "Communities",
      value: String(stats.total),
    },
    {
      key: "platforms",
      label: "Platforms",
      value: String(stats.platformCount || 0),
    },
    {
      key: "avg-members",
      label: "Avg members",
      value: stats.averageMembers
        ? readableNumber(Math.round(stats.averageMembers))
        : "—",
    },
    {
      key: "median-members",
      label: "Median members",
      value: stats.medianMembers
        ? readableNumber(Math.round(stats.medianMembers))
        : "—",
    },
  ]

  return cards
}

function buildBigPictureInsight({ topic, stats }) {
  const name = topic.name
  const lower = name.toLowerCase()
  const total = stats.total
  if (!total) return null

  const soft = total < SAMPLE_VERY_SMALL
  const prefix = soft
    ? "The big picture: In this smaller sample, "
    : "The big picture: "

  const top = stats.platformBreakdown[0]
  const platformCount = stats.platformCount || 0
  const free = stats.accessBreakdown.find(item => item.key === "free")

  if (
    platformCount >= 4 &&
    hasMemberOutlier(stats) &&
    (!top || top.percent < 45)
  ) {
    return `${prefix}${lower} communities are broad and fragmented, with many small groups and a few very large outliers.`
  }
  if (top && top.percent >= 50 && hasMemberOutlier(stats)) {
    return `${prefix}${lower} listings cluster on ${top.name}, while a few large groups pull member averages up.`
  }
  if (top && top.percent >= 50) {
    return `${prefix}much of the ${lower} landscape on Hive Index sits on ${top.name}.`
  }
  if (hasMemberOutlier(stats)) {
    return `${prefix}${lower} has many smaller groups and a few very large outliers pulling averages up.`
  }
  if (free && free.percent >= 75) {
    return `${prefix}most ${lower} communities are free to join, so the space is easy to explore.`
  }
  if (platformCount >= 5) {
    return `${prefix}${lower} is spread across ${platformCount} platforms with no single host dominating.`
  }
  if (top && platformCount >= 3) {
    return `${prefix}${lower} spans several platforms, led by ${top.name}.`
  }

  return null
}

function buildWhatThisMeans({ stats, bigPicture }) {
  const bullets = []
  const top = stats.platformBreakdown[0]
  const platformCount = stats.platformCount || 0
  const free = stats.accessBreakdown.find(item => item.key === "free")

  if (platformCount >= 4 && (!top || top.percent < 40)) {
    bullets.push("The category is fragmented across many platforms.")
  } else if (top && top.percent >= 50) {
    bullets.push(
      `${top.name} concentrates much of the supply in this category.`
    )
  }

  if (hasMemberOutlier(stats)) {
    bullets.push(
      "The average member count is pulled up by a few very large communities."
    )
  }

  if (free && free.percent >= 70) {
    bullets.push(
      "Most communities are free to join, making the space easy to explore."
    )
  }

  if (stats.platformSupplyDemand?.available) {
    const items = stats.platformSupplyDemand.items || []
    const topSupply = items[0]
    const topClicks = [...items].sort((a, b) => b.clicks - a.clicks)[0]
    if (topSupply && topClicks && topSupply.slug !== topClicks.slug) {
      bullets.push(
        "Click behavior suggests visitors gravitate toward familiar platforms."
      )
    } else if (topClicks && topClicks.clickPercent >= 40) {
      bullets.push(
        `Visitors mostly click through on ${topClicks.name} in this category.`
      )
    }
  }

  const topFeature = stats.commonFeatures[0]
  if (topFeature && topFeature.percent >= 50) {
    bullets.push(
      `${topFeature.name} is a standard offering across most listings.`
    )
  }

  const filtered = bullets.filter(bullet => {
    if (!bigPicture) return true
    const bp = bigPicture.toLowerCase()
    const bl = bullet.toLowerCase()
    if (bl.includes("fragmented") && bp.includes("fragment")) return false
    if (bl.includes("average member") && bp.includes("outlier")) return false
    if (bl.includes("free to join") && bp.includes("free")) return false
    if (bl.includes("concentrates") && bp.includes(" sits on ")) return false
    return true
  })

  if (filtered.length < 3) {
    return { available: false, bullets: [] }
  }

  return { available: true, bullets: filtered.slice(0, 4) }
}

function buildExecutiveSummaryModule({ topic, stats, updatedDate }) {
  const hero = buildHeroContent({ topic, stats, updatedDate })
  const statCards = buildExecutiveStatCards({ stats })
  const bigPicture = buildBigPictureInsight({ topic, stats })

  if (!stats.total) {
    return {
      available: false,
      hero,
      intro: null,
      statCards: [],
      bigPicture: null,
      sourceLine: hero.sourceLine,
    }
  }

  const sourceLine = updatedDate
    ? `Source: Hive Index community database · Updated ${updatedDate}`
    : "Source: Hive Index community database · Updated periodically"

  return {
    available: true,
    intro: hero.lead,
    bigPicture,
    statCards,
    sourceLine,
    hero,
  }
}

function buildExecutiveSummaryBullets({ topic, stats }) {
  const topicName = topic.name
  const lower = topicName.toLowerCase()
  const findings = []
  const total = stats.total
  const prefix = datasetOpener(total)

  const topPlatform = stats.platformBreakdown[0]
  if (topPlatform) {
    findings.push(
      `${prefix}${topPlatform.name} is the leading platform for ${lower} communities in this dataset (${topPlatform.percent}%).`
    )
  }

  if (hasMemberOutlier(stats)) {
    findings.push(memberOutlierCopy(stats))
  } else if (stats.averageMembers && stats.medianMembers) {
    const avg = Math.round(stats.averageMembers)
    const med = Math.round(stats.medianMembers)
    findings.push(
      `${prefix}listed communities average ${readableNumber(
        avg
      )} members, with a median of ${readableNumber(med)}.`
    )
  }

  const free = stats.accessBreakdown.find(item => item.key === "free")
  if (free) {
    findings.push(
      `${prefix}${free.percent}% of ${lower} communities are free to join.`
    )
  }

  const topFeature = stats.commonFeatures[0]
  if (topFeature) {
    findings.push(
      `${prefix}${topFeature.name} is the most common feature (${topFeature.percent}%).`
    )
  }

  if (stats.trafficStats.topicRedirects) {
    findings.push(
      `Hive Index users click into ${lower} communities about ${readableNumber(
        stats.trafficStats.topicRedirects
      )} times per month.`
    )
  } else if (stats.trafficStats.topicViews) {
    findings.push(
      `The ${topicName} topic page receives about ${readableNumber(
        stats.trafficStats.topicViews
      )} views per month on Hive Index.`
    )
  }

  return findings.slice(0, 5)
}

function buildExecutiveSummaryParagraph({ topic, stats }) {
  const topicName = topic.name
  const sentences = []
  const prefix = datasetOpener(stats.total)

  const topPlatform = stats.platformBreakdown[0]
  const secondPlatform = stats.platformBreakdown[1]
  const topThreeNames = stats.platformBreakdown
    .slice(0, 3)
    .map(item => item.name)

  if (topPlatform && topPlatform.percent >= 50) {
    sentences.push(
      `${prefix}${topPlatform.name} leads with ${
        topPlatform.percent
      }% of listings${
        secondPlatform
          ? `, though ${secondPlatform.name} also has a meaningful share`
          : ""
      }.`
    )
  } else if (topPlatform && secondPlatform) {
    sentences.push(
      `${topicName} is not dominated by one platform. ${
        topPlatform.name
      } leads, but ${joinNames(
        [secondPlatform.name, stats.platformBreakdown[2]?.name].filter(Boolean)
      )} also show up often.`
    )
  } else if (topThreeNames.length >= 2) {
    sentences.push(
      `${prefix}listings are spread across ${joinNames(
        topThreeNames
      )} and other platforms.`
    )
  }

  if (hasMemberOutlier(stats)) {
    sentences.push(memberOutlierCopy(stats))
  } else if (stats.averageMembers && stats.medianMembers) {
    const med = Math.round(stats.medianMembers)
    if (med > 0) {
      sentences.push(
        `Most groups with public size data center around ${readableNumber(
          med
        )} members at the median.`
      )
    }
  }

  const free = stats.accessBreakdown.find(item => item.key === "free")
  if (free && free.percent >= 65) {
    sentences.push(
      `${free.percent}% are free to join, so the space is relatively easy to explore.`
    )
  }

  const chat = stats.commonFeatures.find(feature => feature.slug === "chat")
  const forum = stats.commonFeatures.find(feature => feature.slug === "forum")
  if (chat && chat.percent >= 45) {
    sentences.push(
      `Live chat shows up on ${chat.percent}% of listings, so real-time conversation is a big part of how these groups run.`
    )
  } else if (forum && forum.percent >= 45) {
    sentences.push(
      `Forum-style discussion is common (${forum.percent}% of listings), which fits async conversation and searchable archives.`
    )
  }

  if (sentences.length < 1) return null
  return sentences.slice(0, 3).join(" ")
}

function buildExecutiveSummary({ topic, stats }) {
  const paragraph = buildExecutiveSummaryParagraph({ topic, stats })
  if (!paragraph) return null
  return { paragraph }
}

function buildTrendsInsight(addedTimeline) {
  const items = addedTimeline?.items || []
  if (items.length < 3) return null

  if (addedTimeline.peakCount >= 2 && addedTimeline.peakLabel) {
    return `Hive Index added the most listings during ${addedTimeline.peakLabel}.`
  }

  const midpoint = Math.floor(items.length / 2)
  const firstHalf = items
    .slice(0, midpoint)
    .reduce((sum, item) => sum + item.count, 0)
  const secondHalf = items
    .slice(midpoint)
    .reduce((sum, item) => sum + item.count, 0)

  if (secondHalf >= firstHalf * 1.35 && secondHalf > 0) {
    return "Coverage on Hive Index has picked up in recent months."
  }
  if (firstHalf >= secondHalf * 1.35 && firstHalf > 0) {
    return "Most listings were added earlier; fewer new groups have joined recently."
  }

  return null
}

function buildUserClicksTakeaway(stats) {
  const { userClickInsights, platformSupplyDemand } = stats
  if (!userClickInsights?.available) return null

  const charts = userClickInsights.charts || []
  const formatChart = charts.find(chart => chart.id === "format")
  const featureChart = charts.find(chart => chart.id === "features")
  const platformChart = charts.find(chart => chart.id === "platform")

  if (platformChart?.items.length >= 2) {
    const names = platformChart.items.slice(0, 2).map(item => item.name)
    return `Most clicks go to ${joinNames(names)}.`
  }

  if (formatChart?.items.length >= 2) {
    const names = formatChart.items
      .slice(0, 2)
      .map(item => item.name.toLowerCase())
    return `Most clicks go to ${joinNames(names)} communities.`
  }

  if (featureChart?.items.length >= 1 && !platformSupplyDemand?.available) {
    const top = featureChart.items[0]
    return `Groups with ${top.name.toLowerCase()} picked up ${
      top.percent
    }% of tracked clicks.`
  }

  if (userClickInsights.topCommunities.length >= 2) {
    return "A few listings get most of the clicks; the rest split what is left."
  }

  return null
}

function buildPlatformSupplyDemandInsight(stats) {
  const { platformSupplyDemand, platformBreakdown } = stats
  if (!platformSupplyDemand?.available || !platformSupplyDemand.items.length) {
    return null
  }

  const topSupply = platformBreakdown[0]
  const topDemand = [...platformSupplyDemand.items].sort(
    (a, b) => b.clicks - a.clicks
  )[0]
  if (!topSupply || !topDemand) return null

  if (topSupply.slug === topDemand.slug) {
    return `${topSupply.name} hosts ${topSupply.percent}% of listings and ${topDemand.clickPercent}% of clicks, so supply and interest line up.`
  }

  const demandLead = topDemand.clickPercent - topDemand.communityPercent
  if (demandLead >= 12) {
    return `${topDemand.name} gets ${topDemand.clickPercent}% of clicks but only ${topDemand.communityPercent}% of listings, so visitors click it more than you'd expect from supply alone.`
  }

  const supplyLead = topSupply.communityPercent - (topDemand.clickPercent || 0)
  if (topSupply.slug !== topDemand.slug && supplyLead >= 12) {
    return `${topSupply.name} has the most listings, but ${topDemand.name} leads on clicks.`
  }

  return null
}

function buildFormatMixInsight(stats) {
  if (!stats.formatMix?.available || !stats.formatMix.items.length) return null
  const top = stats.formatMix.items[0]
  const second = stats.formatMix.items[1]
  if (!top || top.percent < 35) return null
  if (second && second.percent >= 20) {
    return `${top.name} is most common (${
      top.percent
    }%), but ${second.name.toLowerCase()} is also well represented (${
      second.percent
    }%).`
  }
  return `Most listings are ${top.name.toLowerCase()} (${top.percent}%).`
}

function buildStatSummaryCards({ stats }) {
  const cards = [
    { key: "total", label: "Communities analyzed", value: String(stats.total) },
    {
      key: "platforms",
      label: "Platforms represented",
      value: String(stats.platformCount || 0),
    },
  ]

  if (stats.averageMembers) {
    cards.push({
      key: "avg-members",
      label: "Average members",
      value: readableNumber(Math.round(stats.averageMembers)),
    })
  }

  if (stats.medianMembers) {
    cards.push({
      key: "median-members",
      label: "Median members",
      value: readableNumber(Math.round(stats.medianMembers)),
    })
  }

  if (stats.trafficStats.topicViews) {
    cards.push({
      key: "topic-views",
      label: "Topic pageviews / mo",
      value: readableNumber(stats.trafficStats.topicViews),
    })
  }

  if (stats.trafficStats.topicVisitors) {
    cards.push({
      key: "topic-visitors",
      label: "Topic visitors / mo",
      value: readableNumber(stats.trafficStats.topicVisitors),
    })
  }

  if (stats.trafficStats.topicRedirects) {
    cards.push({
      key: "topic-clicks",
      label: "Outbound clicks / mo",
      value: readableNumber(stats.trafficStats.topicRedirects),
    })
  }

  const free = stats.accessBreakdown.find(item => item.key === "free")
  if (free && cards.length < 7) {
    cards.push({
      key: "free",
      label: "Free to join",
      value: `${free.percent}%`,
    })
  }

  return cards.slice(0, 7)
}

function buildTakeaways({ topic, stats }) {
  const takeaways = {}
  const prefix = datasetOpener(stats.total)

  const topPlatform = stats.platformBreakdown[0]
  const secondPlatform = stats.platformBreakdown[1]
  const thirdPlatform = stats.platformBreakdown[2]
  const topThreeShare = stats.platformBreakdown
    .slice(0, 3)
    .reduce((sum, item) => sum + item.percent, 0)

  if (topPlatform) {
    if (stats.total < SAMPLE_VERY_SMALL) {
      takeaways.platforms = `${topPlatform.name} leads (${topPlatform.percent}% of listings).`
    } else if (topPlatform.percent >= 50 && secondPlatform) {
      takeaways.platforms = `${topPlatform.name} leads with ${topPlatform.percent}%, but ${secondPlatform.name} also has a meaningful share (${secondPlatform.percent}%).`
    } else if (secondPlatform && thirdPlatform) {
      takeaways.platforms = `${topPlatform.name} leads, but ${secondPlatform.name} and ${thirdPlatform.name} also represent meaningful shares.`
    } else if (topThreeShare >= 60) {
      takeaways.platforms = `The top three platforms account for ${topThreeShare}% of listings, with a long tail beyond them.`
    } else if (stats.platformBreakdown.length >= 3) {
      takeaways.platforms = `${prefix}no single platform dominates; listings are spread across several hosts.`
    }
  }

  if (hasMemberOutlier(stats)) {
    takeaways.size = memberOutlierCopy(stats)
  }

  const free = stats.accessBreakdown.find(item => item.key === "free")
  const restricted = stats.accessBreakdown.find(
    item => item.key === "restricted"
  )
  if (free && restricted) {
    if (restricted.count === 0) {
      takeaways.access = "Every listing is free to join."
    } else if (free.percent >= 70) {
      takeaways.access = "Most communities are free to join."
    } else if (restricted.percent >= 50) {
      takeaways.access =
        "Most listings are paid or require an application to join."
    }
  }

  const chat = stats.commonFeatures.find(feature => feature.slug === "chat")
  const forum = stats.commonFeatures.find(feature => feature.slug === "forum")
  if (chat && chat.percent >= 50) {
    takeaways.features = `${chat.name} appears on ${chat.percent}% of listings.`
  } else if (forum && forum.percent >= 50) {
    takeaways.features = `${forum.name} appears on ${forum.percent}% of listings.`
  } else if (stats.commonFeatures[0]) {
    const top = stats.commonFeatures[0]
    takeaways.features = `${top.name} is the most common feature (${top.percent}%).`
  }

  if (
    stats.userClickInsights?.available ||
    stats.trafficStats.topicRedirects ||
    stats.trafficStats.topByRedirects.total
  ) {
    takeaways.userClicks = buildUserClicksTakeaway(stats)
  }

  if (stats.reviewBreakdown.reviewedCommunityCount >= 5) {
    const fiveStar = stats.reviewBreakdown.items.find(item => item.key === "5")
    if (fiveStar && fiveStar.percent >= 50) {
      takeaways.reviews =
        "Ratings skew positive among reviewed listings, with most at four or five stars."
    } else {
      takeaways.reviews =
        "Review scores vary, so ratings are most useful for comparing individual listings."
    }
  }

  if (stats.foundedTimeline.yearCount >= 3) {
    const peak = stats.foundedTimeline.items.reduce(
      (best, item) => (item.count > (best?.count || 0) ? item : best),
      null
    )
    if (peak?.label) {
      takeaways.founded = `Most listed groups were founded around ${peak.label}.`
    }
  }

  takeaways.platformSupplyDemand = buildPlatformSupplyDemandInsight(stats)
  if (stats.addedTimeline.items.length >= 3) {
    takeaways.trends = buildTrendsInsight(stats.addedTimeline)
  }
  takeaways.formatMix = buildFormatMixInsight(stats)

  return takeaways
}

function buildComparisonTableRows({ stats, siteBenchmarks }) {
  if (!siteBenchmarks) return []

  const rows = []
  const free = stats.accessBreakdown.find(item => item.key === "free")

  if (free && siteBenchmarks.freePercent != null) {
    const topicVal = free.percent
    const siteVal = siteBenchmarks.freePercent
    rows.push({
      metric: "Free to join",
      topic: `${topicVal}%`,
      site: `${siteVal}%`,
      highlight: Math.abs(topicVal - siteVal) >= 8,
    })
  }

  if (stats.medianMembers && siteBenchmarks.medianMembers) {
    const topicVal = Math.round(stats.medianMembers)
    const siteVal = Math.round(siteBenchmarks.medianMembers)
    const ratio = topicVal / siteVal
    rows.push({
      metric: "Median members",
      topic: readableNumber(topicVal),
      site: readableNumber(siteVal),
      highlight: ratio >= 1.4 || ratio <= 0.7,
    })
  }

  if (stats.averageMembers && siteBenchmarks.averageMembers) {
    const topicVal = Math.round(stats.averageMembers)
    const siteVal = Math.round(siteBenchmarks.averageMembers)
    const ratio = topicVal / siteVal
    rows.push({
      metric: "Average members",
      topic: readableNumber(topicVal),
      site: readableNumber(siteVal),
      highlight: ratio >= 1.4 || ratio <= 0.7,
    })
  }

  rows.push({
    metric: "Platforms represented",
    topic: String(stats.platformCount || 0),
    site: null,
    highlight: false,
  })

  const restricted = stats.accessBreakdown.find(
    item => item.key === "restricted"
  )
  if (restricted && siteBenchmarks.restrictedPercent != null) {
    const topicVal = restricted.percent
    const siteVal = siteBenchmarks.restrictedPercent
    rows.push({
      metric: "Paid or apply-to-join",
      topic: `${topicVal}%`,
      site: `${siteVal}%`,
      highlight: Math.abs(topicVal - siteVal) >= 8,
    })
  }

  const chatFeature = stats.commonFeatures.find(item => item.slug === "chat")
  if (chatFeature && siteBenchmarks.chatPercent != null) {
    const topicVal = chatFeature.percent
    const siteVal = siteBenchmarks.chatPercent
    rows.push({
      metric: "Live chat",
      topic: `${topicVal}%`,
      site: `${siteVal}%`,
      highlight: Math.abs(topicVal - siteVal) >= 12,
    })
  }

  const discord = stats.platformBreakdown.find(item => item.slug === "discord")
  if (discord && siteBenchmarks.discordPercent != null) {
    const topicVal = discord.percent
    const siteVal = siteBenchmarks.discordPercent
    rows.push({
      metric: "Discord share",
      topic: `${topicVal}%`,
      site: `${siteVal}%`,
      highlight: Math.abs(topicVal - siteVal) >= 10,
    })
  }

  if (stats.trafficStats.topicViews && siteBenchmarks.avgTopicViews) {
    const topicVal = stats.trafficStats.topicViews
    const siteVal = Math.round(siteBenchmarks.avgTopicViews)
    const ratio = topicVal / siteVal
    rows.push({
      metric: "Topic pageviews / mo",
      topic: readableNumber(topicVal),
      site: readableNumber(siteVal),
      highlight: ratio >= 1.5 || ratio <= 0.65,
    })
  }

  if (stats.trafficStats.topicRedirects) {
    rows.push({
      metric: "Outbound clicks / mo",
      topic: readableNumber(stats.trafficStats.topicRedirects),
      site: null,
      highlight: false,
    })
  }

  return rows.filter(row => row.topic != null)
}

function buildComparisonSummary({ topic, stats, siteBenchmarks, tableRows }) {
  const topicName = topic.name
  const traits = []

  const freeRow = tableRows.find(row => row.metric === "Free to join")
  if (freeRow) {
    const topicFree = parseInt(freeRow.topic, 10)
    const siteFree = parseInt(freeRow.site, 10)
    if (topicFree - siteFree >= 8) {
      traits.push("more free-to-join")
    } else if (siteFree - topicFree >= 8) {
      traits.push("less free-to-join")
    }
  }

  const medianRow = tableRows.find(row => row.metric === "Median members")
  if (medianRow && siteBenchmarks.medianMembers) {
    const ratio = stats.medianMembers / siteBenchmarks.medianMembers
    if (ratio >= 1.4) {
      traits.push("larger")
    } else if (ratio <= 0.7) {
      traits.push("smaller")
    }
  }

  if (stats.platformCount >= 8) {
    traits.push("more platform-diverse")
  }

  const chatRow = tableRows.find(row => row.metric === "Live chat")
  if (chatRow) {
    const topicChat = parseInt(chatRow.topic, 10)
    const siteChat = parseInt(chatRow.site, 10)
    if (topicChat - siteChat >= 12) {
      traits.push("more chat-heavy")
    }
  }

  const restrictedRow = tableRows.find(
    row => row.metric === "Paid or apply-to-join"
  )
  if (restrictedRow) {
    const topicRestricted = parseInt(restrictedRow.topic, 10)
    const siteRestricted = parseInt(restrictedRow.site, 10)
    if (topicRestricted - siteRestricted >= 8) {
      traits.push("more paid or gated")
    }
  }

  if (!traits.length) return null

  if (traits.length === 1) {
    return `${topicName} is ${traits[0]} than typical Hive Index topics.`
  }

  const last = traits.pop()
  return `${topicName} is ${traits.join(
    ", "
  )}, and ${last}, compared with typical Hive Index topics.`
}

function buildTopicComparison({ topic, stats, siteBenchmarks }) {
  if (!siteBenchmarks || stats.total < 5) {
    return { available: false, tableRows: [], summary: null }
  }

  const tableRows = buildComparisonTableRows({ stats, siteBenchmarks })
  const comparableRows = tableRows.filter(row => row.site != null)

  if (comparableRows.length < 2) {
    return { available: false, tableRows: [], summary: null }
  }

  const summary = buildComparisonSummary({
    topic,
    stats,
    siteBenchmarks,
    tableRows: comparableRows,
  })

  return {
    available: true,
    intro: "Benchmarked against the average Hive Index topic",
    tableRows: comparableRows.slice(0, 6),
    summary,
  }
}

function buildQualitySection({ topic, stats, platforms }) {
  const reviewedCount = stats.reviewBreakdown.reviewedCommunityCount
  if (!reviewedCount) {
    return { available: false }
  }

  const mode = reviewedCount >= 5 ? "full" : "limited"

  const platformBySlug = {}
  ;(platforms || []).forEach(platform => {
    platformBySlug[platform.slug] = platform
  })

  const enrich = community => {
    const slugs = platformSlugs(community)
    const primarySlug = slugs[0]
    return {
      slug: community.slug,
      name: community.name,
      reviewAvg: community.reviewAvg,
      reviewCount: community.reviewCount,
      members: community.members > 0 ? readableNumber(community.members) : null,
      platformName:
        (platformBySlug[primarySlug] && platformBySlug[primarySlug].name) ||
        (primarySlug === "independent" ? "Independent" : primarySlug),
    }
  }

  const exampleLimit = mode === "limited" ? 2 : 3
  const examples = stats.highestRatedCommunities
    .slice(0, exampleLimit)
    .map(enrich)
    .filter(example => example.slug)

  let featureInsight = null
  const highRatedFeatures =
    mode === "full" && stats.highRatedFeatures?.available
      ? stats.highRatedFeatures
      : null

  if (highRatedFeatures) {
    const names = highRatedFeatures.items
      .slice(0, 3)
      .map(item => item.name.toLowerCase())
    featureInsight = `Among reviewed communities, highly rated groups often include ${joinNames(
      names
    )}.`
  }

  const limitedNotice =
    mode === "limited"
      ? `Only ${reviewedCount} ${communityWord(reviewedCount)} ${
          reviewedCount === 1 ? "has" : "have"
        } ratings so far, so read this as a small sample.`
      : null

  return {
    available: true,
    mode,
    limitedNotice,
    examples,
    highRatedFeatures,
    featureInsight,
  }
}

function buildMethodology({ topic, stats, updatedDate }) {
  const topicName = topic.name
  const total = stats.total
  const sampleNote = getSampleSizeNote(total)

  const bullets = []
  if (sampleNote) {
    bullets.push(sampleNote)
  }
  if (updatedDate) {
    bullets.push(`Last updated ${updatedDate}`)
  }

  return {
    paragraph: `This report uses communities in the Hive Index database tagged as ${topicName}. We exclude removed or suppressed listings. Member counts, traffic, features, reviews, and ratings may vary by source and are updated periodically.`,
    bullets,
    updatedDate,
  }
}

function buildFAQ({ topic, stats }) {
  const topicName = topic.name
  const lower = topicName.toLowerCase()
  const total = stats.total
  const topPlatform = stats.platformBreakdown[0]
  const free = stats.accessBreakdown.find(item => item.key === "free")
  const topFeature = stats.commonFeatures[0]

  const faqs = []

  if (topic.description) {
    faqs.push({
      question: `What are ${lower} communities?`,
      answer: topic.description.trim().endsWith(".")
        ? topic.description.trim()
        : `${topic.description.trim()}.`,
    })
  } else {
    faqs.push({
      question: `What are ${lower} communities?`,
      answer: `Online groups where people connect around ${lower}-related interests, often through chat, forums, events, or membership platforms.`,
    })
  }

  if (topPlatform) {
    faqs.push({
      question: `Where do most ${lower} communities live?`,
      answer: `${topPlatform.name} is the most common platform in this report (${topPlatform.percent}% of ${total} listings).`,
    })
  }

  if (free) {
    faqs.push({
      question: "Are most of these communities free?",
      answer: `${free.percent}% are free to join. The rest require payment, an application, or both.`,
    })
  }

  if (stats.averageMembers) {
    const avg = readableNumber(Math.round(stats.averageMembers))
    const med = stats.medianMembers
      ? readableNumber(Math.round(stats.medianMembers))
      : null
    let sizeAnswer = med
      ? `Among listings with public size data, the average is ${avg} members and the median is ${med}.`
      : `Among listings with public size data, the average is ${avg} members.`
    if (hasMemberOutlier(stats) && med) {
      sizeAnswer += ` The median is usually the better signal for a typical group because a few large ones inflate the average.`
    }
    faqs.push({
      question: `How big is a typical ${lower} community?`,
      answer: sizeAnswer,
    })
  }

  if (topFeature) {
    const otherFeatures =
      joinNames(
        stats.commonFeatures.slice(1, 4).map(item => item.name.toLowerCase())
      ) || "events and member resources"
    faqs.push({
      question: "What do these communities usually include?",
      answer: `${topFeature.name} is the most common (${topFeature.percent}%). Others that show up often include ${otherFeatures}.`,
    })
  }

  faqs.push({
    question: "How does Hive Index collect this data?",
    answer:
      "Listings are submitted by community builders and researchers, reviewed for relevance, and updated with platform, feature, member, review, and traffic data where available.",
  })

  faqs.push({
    question: "Where can I see the full list?",
    answer: `The full directory is on Hive Index's ${topicName} topic page, where you can filter by platform, size, features, and more.`,
  })

  return faqs.slice(0, 7)
}

function buildRelatedLinkGroups({ topic, relatedTopics, platformBreakdown }) {
  const related = (relatedTopics || []).slice(0, 6).map(link => ({
    href: link.href,
    label: link.label || link.name,
  }))
  const hasMoreRelated = (relatedTopics || []).length > 6

  const bySlug = {}
  ;(platformBreakdown || []).forEach(item => {
    bySlug[item.slug] = item
  })

  const platformLinks = []
  PLATFORM_LINK_PRIORITY.forEach(slug => {
    if (!bySlug[slug]) return
    platformLinks.push({
      href: `/topics/${topic.slug}/platform/${slug}/`,
      label: `${topic.name} ${bySlug[slug].name}`,
    })
  })
  ;(platformBreakdown || [])
    .filter(item => !PLATFORM_LINK_PRIORITY.includes(item.slug))
    .sort((a, b) => b.count - a.count)
    .slice(0, 3)
    .forEach(item => {
      if (platformLinks.length >= 5) return
      platformLinks.push({
        href: `/topics/${topic.slug}/platform/${item.slug}/`,
        label: `${topic.name} ${item.name}`,
      })
    })

  return {
    relatedTopics: related,
    platformLinks: platformLinks.slice(0, 5),
    moreRelatedHref: hasMoreRelated ? `/topics/${topic.slug}/` : null,
  }
}

function buildExploreFooter({ topic, relatedLinks, relatedTopics }) {
  const topicName = topic.name

  const reports = (relatedTopics || [])
    .filter(related => related.count == null || related.count > 0)
    .slice(0, 4)
    .map(related => ({
      href: `/topics/${related.slug}/stats/`,
      label: `${related.name} Communities Report`,
    }))

  const related = (relatedLinks?.relatedTopics || []).slice(0, 4)
  const browseByPlatform = (relatedLinks?.platformLinks || []).slice(0, 4)

  const browse = [
    {
      href: `/topics/${topic.slug}/`,
      label: `All ${topicName} communities`,
    },
  ]

  return {
    title: "Keep exploring",
    browse,
    reports,
    relatedTopics: related,
    browseByPlatform,
  }
}

function buildReportContent({
  topic,
  stats,
  communities,
  platforms,
  relatedTopics,
  siteBenchmarks,
  updatedDate,
}) {
  const filteredRelated = (relatedTopics || [])
    .filter(related => related && related.slug && related.name)
    .map(related => ({
      slug: related.slug,
      name: related.name,
      count: related.count,
      href: `/topics/${related.slug}/`,
    }))

  const relatedLinks = buildRelatedLinkGroups({
    topic,
    relatedTopics: filteredRelated,
    platformBreakdown: stats.platformBreakdown,
  })

  const executiveSummary = buildExecutiveSummaryModule({
    topic,
    stats,
    updatedDate,
  })

  return {
    meta: buildReportMeta({ topic, stats }),
    executiveSummary,
    takeaways: buildTakeaways({ topic, stats }),
    comparison: buildTopicComparison({ topic, stats, siteBenchmarks }),
    quality: buildQualitySection({ topic, stats, platforms }),
    whatThisMeans: buildWhatThisMeans({
      stats,
      bigPicture: executiveSummary.bigPicture,
    }),
    methodology: buildMethodology({ topic, stats, updatedDate }),
    faq: buildFAQ({ topic, stats }),
    relatedLinks,
    exploreFooter: buildExploreFooter({
      topic,
      relatedLinks,
      relatedTopics: filteredRelated,
    }),
  }
}

export { buildReportContent }
